/**
 * What SendFormPanel, SendTextPanel and SendEmailPanel share beyond PatientSidebar (Patient Action
 * Panels, P1, 2026-10-09). Each panel `extends` PatientSidebar — the classic popup's sending, template
 * loading and credit logic run unchanged — and lists this as a `mixin`, which Vue applies AFTER the
 * extended component, so what is defined here wins over PatientSidebar's own methods.
 *
 * It carries the three fixes the classic popup keeps on the to-do list (they are made HERE ONLY; the
 * classic PatientSidebar.vue is deliberately untouched):
 *
 *   1. A FAILED EMAIL MUST NOT SAY "QUEUED". `teamio/sendEmailMsg` swallows its own error and
 *      resolves `undefined` (src/store/teamio.js sendEmailMsg), and every classic email path then
 *      closes the popup and toasts "Email queued". The fix is at the one seam all three classic
 *      email methods share — `await this.sendEmailMsg(payload)` — so nothing else is copied:
 *      `sendEmailMsg` below THROWS when the dispatch returns nothing, and the classic method's own
 *      catch keeps the panel open with the error. Because the classic methods still build the
 *      payload, the keys / eventType / customVars cannot drift from the classic ones
 *      (cypress/node-tests/patient-send-panels.test.js pins both halves).
 *
 *   2. FORMS BY EMAIL skipped the email-credit block and emailComposeProblems / findUnknownVariables
 *      that the composed email applies. `sendCustomEmail` here refuses first with the same checks,
 *      then runs the classic method.
 *
 *   3. (Forms vs Wiki template can never both be selected — that one is SendFormPanel's own switch.)
 *
 * Also here, because two or three panels need them: the busy / error state the panels draw (the
 * classic popup used a full-screen overlay and PopupErrorDisplay), and WHY a send is unavailable —
 * the text one routes through the shared opt-out rule (utils/textConsent), exactly as PatientSidebar's
 * sendText does when it refuses.
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings — the panels inherit src/mixin.js through
 * PatientSidebar, whose computeds would replace it (mixin-prop-shadow-guardrail).
 */
import PatientSidebar from '@/pages/PatientProfile/components/PatientSidebar';
import { emailComposeProblems } from '@/utils/emailBody.js';
import { findUnknownVariables, describeUnknownVariables } from '@/utils/templateVariables.js';
import { getTextBlockReason } from '@/utils/textConsent.js';
import { describePhoneProblem } from '@/utils/patientPhone.js';
import { SMS_DISABLED_MESSAGE } from '@/utils/siteCapability.js';

/** "(201) 734-5305" for a 10- or 11-digit US number; anything else is shown as saved. */
export function formatPhoneLine(raw) {
  const text = String(raw == null ? '' : raw).trim();
  const digits = text.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return text;
}

export default {
  computed: {
    /** "Colette Danielson" — the header's patient-name line (already masked at the data boundary). */
    apPatientName() {
      const p = this.patientData || {};
      const name = [p.FName, p.LName].filter(Boolean).join(' ').trim();
      return name || p.PatientName || '';
    },

    /** A send is in flight (the classic popup drew a full-screen overlay for this; the panels grey the button). */
    apBusy() {
      return !!this.loading2 && !this.initialLoading;
    },

    /** The failure the panel shows, '' when none. The store holds a string, or an Error from the classic catch blocks. */
    apErrorText() {
      const state = this.$store.state.alert;
      if (!state || !state.errored2) return '';
      const m = state.message2;
      const text = m && typeof m === 'object' && m.message ? m.message : String(m == null ? '' : m);
      return text.trim() || 'Something went wrong. Please try again.';
    },

    apTimezone() {
      const s = this.allSettings && this.allSettings.GENERAL_TIMEZONE;
      return (s && s.PropertyValue) || undefined;
    },

    /**
     * Why a text cannot go to this patient, '' when it can. The same questions, in the same order, as
     * PatientSidebar.sendText asks before it refuses — asked up front so Send is off and says why.
     * Opt-out comes from the ONE shared rule (utils/textConsent), never an inline TxtMsgOk check.
     */
    smsUnavailableReason() {
      if (!this.smsFeatureEnabled) return 'SMS is not set up for your account. Please contact TeamIO support to turn it on.';
      if (this.smsOutboundOff) return SMS_DISABLED_MESSAGE;
      if (!this.canSendSMS) {
        const block = getTextBlockReason({ patient: this.patientData, timezone: this.apTimezone });
        if (block.blocked) return block.message;
        return describePhoneProblem(this.patientData && this.patientData.WirelessPhone)
          || 'Cannot send SMS to this patient. Please verify the patient has a valid phone number in Open Dental.';
      }
      return '';
    },

    /** Why an email cannot go to this patient, '' when it can (PatientSidebar.sendComposedEmail's first two refusals). */
    emailUnavailableReason() {
      if (!this.emailFeatureEnabled) return 'Email is not set up for your account. Please contact TeamIO support to turn it on.';
      if (!this.canSendEmail) return 'Cannot send email to this patient. Please verify the patient has a valid email address in Open Dental.';
      return '';
    },
  },

  watch: {
    // The error notice sits at the TOP of the panel's body, but Send is at the bottom of it: on a short
    // screen the user has scrolled down to press Send, so a failure would appear out of sight and the
    // click would look like it did nothing (found by patient-action-panels.cy.js at 1280x720). Bring it
    // into view when it appears; `nearest` does not move a notice that is already on screen.
    apErrorText(text) {
      if (!text) return;
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector && this.$el.querySelector('[data-test="send-panel-error"]');
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
      });
    },
  },

  methods: {
    apDismissError() {
      this.resetAllErrors();
    },

    /** The back arrow: return from a send step to the list, clearing a stale error. */
    apBack() {
      this.resetAllErrors();
      this.currentStep = 'step1';
    },

    /**
     * FIX 1 — the seam. PatientSidebar's sendComposedEmail / sendCustomEmail / sendEmailWithWiki all do
     * `await this.sendEmailMsg(payload)` and then close the popup and toast "queued". The store action
     * swallows a failure and resolves undefined, so a falsy result IS a failure: throw, and the classic
     * method's catch shows the error and leaves the panel open. The store has already put the server's
     * wording in alert.message; use it.
     */
    async sendEmailMsg(payload) {
      const result = await this.$store.dispatch('teamio/sendEmailMsg', payload);
      if (!result) {
        const reason = this.$store.state.alert && this.$store.state.alert.message;
        throw new Error(typeof reason === 'string' && reason.trim() ? reason.trim() : 'Failed to send email. Please try again.');
      }
      return result;
    },

    /** FIX 2 — the same refusals sendComposedEmail makes (out of credits, a variable nobody fills), then the classic send. */
    async sendCustomEmail() {
      this.resetAllErrors();
      const problems = this.formEmailProblems();
      if (problems.length) {
        this.setErrored2(true);
        this.setMessage2(problems.join(' '));
        return false;
      }
      return PatientSidebar.methods.sendCustomEmail.call(this);
    },

    formEmailProblems() {
      const siteInfo = this.getSite(this.siteID);
      const unknown = findUnknownVariables(this.emailMessage || '');
      return emailComposeProblems({
        // The forms email's subject is fixed (the classic method sets it); only the other checks matter.
        subject: `Forms from ${siteInfo && siteInfo.Name ? siteInfo.Name : 'your dental office'}`,
        message: this.emailMessage,
        greeting: this.emailGreeting,
        unknownVariables: unknown,
        unknownVariablesMessage: describeUnknownVariables(unknown),
        walletOn: this.emailWalletOn,
        creditsAvailable: this.emailCreditsAvailable,
      });
    },
  },
};
